import { Box, Heading, Skeleton, useToast } from '@chakra-ui/react';
import React from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { useGetBorrowingBaseReportQuery } from 'packages/borrowing-base/requests/borrowingBase.gql.generated';
import { BorrowingBaseSummaryWithSingleValue } from 'packages/borrowing-base/ui/borrowingBaseSummary/BorrowingBaseSummaryWithSingleValue';
import { BorrowingBaseSummarySections } from 'packages/borrowing-base/ui/borrowingBaseSummary/utils';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

const borrowingBaseReportDisplaySections: BorrowingBaseSummarySections[] = [
  BorrowingBaseSummarySections.receivables,
  BorrowingBaseSummarySections.cash,
  BorrowingBaseSummarySections.finalBorrowingBaseReport,
];

type Props = {
  borrowingBaseReportId: string;
  creditFacilityId: string;
  hasTitle?: boolean;
};
export const FundingRequestDetailBorrowingBaseSummary = ({
  borrowingBaseReportId,
  creditFacilityId,
  hasTitle = true,
}: Props) => {
  const toast = useToast();

  const {
    data: borrowingBaseReportData,
    loading: isBorrowingBaseReportLoading,
  } = useGetBorrowingBaseReportQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { borrowingBaseReportId },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId },
    fetchPolicy: 'network-only',
  });

  if (
    !borrowingBaseReportData ||
    isBorrowingBaseReportLoading ||
    !creditAgreementData ||
    isCreditAgreementLoading
  ) {
    return <Skeleton height="600px" />;
  }
  const creditAgreementString =
    creditAgreementData?.currentCreditAgreementFullArtifact?.creditAgreement;
  const creditAgreementId =
    creditAgreementData?.currentCreditAgreementFullArtifact?.id;

  let parsedCreditAgreement = null;
  if (creditAgreementString) {
    try {
      parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );
    } catch (err) {
      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
      });
    }
  }
  finleyAssert(
    parsedCreditAgreement,
    `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );

  return (
    <>
      {hasTitle && (
        <Heading as="h3" fontSize="2xl" mt="40px" mb="24px">
          Borrowing Base
        </Heading>
      )}
      <Box
        padding="24px"
        borderRadius="8px"
        border={`1px solid ${colors.gray[200]}`}
        backgroundColor="white"
      >
        <BorrowingBaseSummaryWithSingleValue
          sections={borrowingBaseReportDisplaySections}
          borrowingBaseReport={borrowingBaseReportData.borrowingBaseReport}
          creditAgreement={parsedCreditAgreement}
        />
      </Box>
    </>
  );
};
